import { headerAndFooter, constants } from '@config';

import GestureAbstract from './abstract';

export default class GestureMove extends GestureAbstract {
  constructor (canvas) {
    super(canvas);

    // The DOM element that was just selected
    this.justSelected = null;
    this.previouslySelected = null;
  }

  #getTranslateLimit ({ canvas, mouseTarget }) {
    const layerName = canvas.getCurrentLayerName();
    const limitedLayers = [headerAndFooter.header, headerAndFooter.footer];
    const layer = limitedLayers.find(({ layer }) => layer === layerName);
    if (!layer) return;

    const rect = document.getElementById(layer.rectId);
    if (!rect) return;
    const areaSize = document.getElementById(layer.rectId).getBBox();
    const targetSize = mouseTarget.getBBox();

    return {
      minX: areaSize.x + (targetSize.x * -1),
      minY: areaSize.y + (targetSize.y * -1),
      maxX: areaSize.x + (areaSize.width - targetSize.x) - targetSize.width,
      maxY: areaSize.y + (areaSize.height - targetSize.y) - targetSize.height
    };
  }

  #isSelectable ({ mouseTarget }) {
    if (!mouseTarget) return false;

    return !mouseTarget.classList.contains('non-selectable') && !mouseTarget.closest('.non-selectable');
  }

  init (mouseX, mouseY, mouseTarget, rightClick, event) {
    super.init(mouseX, mouseY, mouseTarget, rightClick, event);
    const shiftKey = event.shiftKey || event.metaKey;
    const ctrlKey = event.ctrlKey || event.metaKey;
    const that = this;
    const canvas = that.canvas;
    const currentZoom = canvas.getZoom();
    const svgroot = canvas.getRootElem();
    const selectedElements = canvas.getSelectedElems();

    this.previouslySelected = _.clone(selectedElements);
    this.limit = this.#getTranslateLimit({ canvas, mouseTarget });

    canvas.started = !(rightClick);
    canvas.current_resize_mode = 'none';
    canvas.runExtensions('startMove', {
      mouseTarget,
      selectedElements
    });

    const isCroppingBackground = mouseTarget === svgroot && selectedElements[0] &&
    $(selectedElements).attr('data-old-id') === constants.background.image;

    if (mouseTarget !== svgroot || isCroppingBackground) {
    // if this element is not yet selected, clear selection and select it
      if (selectedElements.indexOf(mouseTarget) === -1 && !isCroppingBackground) {
      // only clear selection if shift is not pressed (otherwise, add
      // element to selection)
        if (!shiftKey && !ctrlKey) {
        // No need to do the call here as it will be done on addToSelection
          canvas.clearSelection(true);
        }
        INKIVE.C.IS_MOBILE && $('#selectorParentGroup').addClass('hidden');

        if (this.#isSelectable({ mouseTarget })) {
          canvas.addToSelection([mouseTarget]);
          that.justSelected = mouseTarget;
        }
        canvas.pathActions.clear();
      } else if (shiftKey || ctrlKey) {
        canvas.removeFromSelection([mouseTarget]);
      }
      // else if it's a path, go into pathedit mode in mouseup

      if (!rightClick && this.#isSelectable({ mouseTarget })) {
      // insert a dummy transform so if the element(s) are moved it will have
      // a transform to use for its translate
        for (let i = 0; i < selectedElements.length; ++i) {
          if (!selectedElements[i] === null) {
            continue;
          }

          const transformList = svgedit.transformlist.getTransformList(selectedElements[i]);

          if (transformList) {
            if (transformList.numberOfItems) {
              transformList.insertItemBefore(svgroot.createSVGTransform(), 0);
            } else {
              transformList.appendItem(svgroot.createSVGTransform());
            }
          }
        }
      }
    } else if (!rightClick) {
      const stopExecution = INKIVE.C.IS_MOBILE &&
      selectedElements.length &&
      !$(selectedElements[0]).parent().is('.background');

      if (stopExecution) {
        canvas.clearSelection();
        return;
      }

      if (!shiftKey && !ctrlKey) canvas.clearSelection();
      canvas.setMode('multiselect');

      if (canvas.rubberBox == null) {
        canvas.rubberBox = canvas.selectorManager.getRubberBandBox();
      }

      that.r_start_x *= currentZoom;
      that.r_start_y *= currentZoom;

      svgedit.utilities.assignAttributes(canvas.rubberBox, {
        x: that.r_start_x,
        y: that.r_start_y,
        width: 0,
        height: 0,
        display: 'inline'
      }, 100);
    }
  }

  update (rMouseX, rMouseY, event) {
    const shiftKey = event && event.shiftKey;
    const ctrlKey = event && (event.ctrlKey || event.metaKey);
    const that = this;
    const canvas = that.canvas;
    const currentZoom = canvas.getZoom();

    rMouseX = rMouseX / currentZoom;
    rMouseY = rMouseY / currentZoom;

    let mouseX = rMouseX;
    let mouseY = rMouseY;

    let selectedElements = canvas.getSelectedElems();

    let dx = 0; let dy = 0;

    const currentMode = canvas.getMode();
    const rf = INKIVE.RestrictionsFacade;
    switch (currentMode) {
      case 'select':
        if (INKIVE.C.IS_MOBILE) {
          const isMultitouch = event.touches && event.touches.length > 1;

          if (this.checkMobileDragOrTap(mouseX, mouseY, isMultitouch)) {
            selectedElements = canvas.getSelectedElems();
          } else {
            return;
          }
        }

        // we temporarily use a translate on the element(s) being dragged
        // this transform is removed upon mousing up and the element is
        // relocated to the new location
        if (selectedElements[0] !== null) {
          if (shiftKey) {
            const xya = svgedit.math.snapToAngle(that.start_x, that.start_y, mouseX, mouseY);
            mouseX = xya.x;
            mouseY = xya.y;
          }

          if (mouseX !== 0 || mouseY !== 0) {
            for (let i = 0, len = selectedElements.length; i < len; ++i) {
              const selected = selectedElements[i];
              if (selected == null) {
                break;
              }

              const spanned = this.getSnappendCoordinates(
                (mouseX - that.start_x), // dx
                (mouseY - that.start_y), // dy
                function () { // snap point
                  const bb = svgedit.utilities.getBBox(selected);
                  return [bb.x, bb.y];
                }
              );

              let restrictedMovement = rf.applyRestriction(selected, 'margin', {
                x: spanned[0],
                y: spanned[1]
              });

              restrictedMovement = rf.applyRestriction(selected, 'movement', restrictedMovement);

              dx = restrictedMovement.x;
              dy = restrictedMovement.y;

              if (this.limit) {
                dx = dx > 0
                  ? Math.min(dx, this.limit.maxX)
                  : Math.max(dx, this.limit.minX);
                dy = dy > 0
                  ? Math.min(dy, this.limit.maxY)
                  : Math.max(dy, this.limit.minY);
              }

              // For layout Clipper
              if ($(selected).hasClass('layoutClipperImage')) {
                const result = canvas.runExtensions('moveLayoutClipperImage', {
                  dx,
                  dy,
                  selected
                });

                dx = result.dx;
                dy = result.dy;
              } else if ($(selected).hasClass('backgroundTransitionLimit')) {
                const bb = svgedit.utilities.getBBox(selected);
                // left
                if (bb.x + dx <= 0) {
                  dx = -bb.x;
                }
                // right
                if (bb.x + bb.width + dx >= canvas.contentW) {
                  dx = canvas.contentW - (bb.x + bb.width);
                }
                // top
                if (bb.y + dy <= 0) {
                  dy = -bb.y;
                }
                // bottom
                if (bb.y + bb.height + dy >= canvas.contentH) {
                  dy = canvas.contentH - (bb.y + bb.height);
                }
              }

              // update the dummy transform in our transform list
              // to be a translate
              const svgroot = canvas.getRootElem();
              const xform = svgroot.createSVGTransform();
              const tlist = svgedit.transformlist.getTransformList(selected);

              // Note that if Webkit and there's no ID for this
              // element, the dummy transform may have gotten lost.
              // This results in unexpected behaviour
              xform.setTranslate(dx, dy);

              if (tlist.numberOfItems) {
                tlist.replaceItem(xform, 0);
              } else {
                tlist.appendItem(xform);
              }

              // update our internal bbox that we're tracking while dragging
              const selector = canvas.selectorManager.requestSelector(selected);
              selector.resize();
              selector.fadeSelectionGrips(false);

              if ($(selected).hasClass('layoutClipperImage')) {
                const layoutScreen = $(selected).siblings('#layoutScreen')[0];
                layoutScreen && svgedit.recalculate.cloneTransfromList(selected, layoutScreen);
              }
            }
          }
        }

        break;

      case 'multiselect': {
        rMouseX *= currentZoom;
        rMouseY *= currentZoom;

        svgedit.utilities.assignAttributes(canvas.rubberBox, {
          x: Math.min(that.r_start_x, rMouseX),
          y: Math.min(that.r_start_y, rMouseY),
          width: Math.abs(rMouseX - that.r_start_x),
          height: Math.abs(rMouseY - that.r_start_y)
        }, 100);

        // for each selected:
        // - if newList contains selected, do nothing
        // - if newList doesn't contain selected, remove it from selected
        // - for any newList that was not in selectedElements, add it to selected
        const elemsToRemove = [];
        const elemsToAdd = [];
        const newList = canvas.getIntersectionList();

        for (let i = 0, len = selectedElements.length; i < len; ++i) {
          const ind = newList.indexOf(selectedElements[i]);
          if (ind === -1) {
            elemsToRemove.push(selectedElements[i]);
          } else {
            newList[ind] = null;
          }
        }

        for (let i = 0, len = newList.length; i < len; ++i) {
          if (newList[i] && !newList[i].classList.contains('non-selectable')) {
            elemsToAdd.push(newList[i]);
          }
        }

        if (elemsToRemove.length > 0 && !shiftKey && !ctrlKey) {
          canvas.removeFromSelection(elemsToRemove, true);
        }

        if (elemsToAdd.length > 0) {
          canvas.addToSelection(elemsToAdd, false, true);
        }

        break;
      }
    }

    return [dx, dy];
  }

  destruct () {
    super.destruct();

    const canvas = this.canvas;
    const selectedElements = canvas.getSelectedElems();
    const currentMode = canvas.getMode();

    INKIVE.C.IS_MOBILE && $('#selectorParentGroup').removeClass('hidden');
    this.justSelected = null;
    let changes, isLayoutClipperImage;

    switch (currentMode) {
      case 'multiselect':
        if (canvas.rubberBox != null) {
          canvas.rubberBox.setAttribute('display', 'none');
          canvas.curBBoxes = [];
        }

        canvas.setMode('select');
        break;

      case 'select': {
        if (selectedElements[0] != null) {
          if (selectedElements[1] == null) {
            $(selectedElements[0]).removeClass('moving');
            const selector = canvas.selectorManager.requestSelector(selectedElements[0]);

            if ($(selectedElements[0]).is('.non-selectable')) {
              selector.selectorGroup.setAttribute('display', 'none');
              return;
            }

            selector.showGrips(true);
            selector.fadeSelectionGrips(true);
            selector.resize();
          }

          changes = canvas.recalculateAllSelectedDimensions();
          let i; const len = selectedElements.length;
          for (i = 0; i < len; ++i) {
            if (selectedElements[i] == null) break;
            if (!selectedElements[i].firstChild) {
              canvas.selectorManager.requestSelector(selectedElements[i]).resize();
            }
          }

          if (svgedit.browser.supportsNonScalingStroke()) {
            const elem = selectedElements[0];
            if (elem) {
              svgedit.sanitize.sanitizeSvgStyle(elem);
              svgedit.utilities.walkTree(elem, function (elem) {
                svgedit.sanitize.sanitizeSvgStyle(elem);
              });
            }
          }

          _.each(selectedElements, function (elem) {
            if (elem?.classList?.contains('layoutClipperImage')) {
              isLayoutClipperImage = true;
            }
            canvas.runExtensions('endMove', { elem });
          }
          );
        }
      }
    }

    if (changes && changes.length && !isLayoutClipperImage) {
      // There is a bug that it detect wrong change when the old
      // value of transform is empty string and new value of transform is null
      const isFakeChange = this.checkFakeChange(changes);

      isFakeChange || INKIVE.DesignFacade.trigger('move');
    }
  }

  checkFakeChange (changes) {
    if (changes.length !== 1) return false;

    const newChange = changes[0]?.stack[0]?.newValues;
    const oldChange = changes[0]?.stack[0]?.oldValues;

    if (!newChange || !oldChange) return false;

    if (Object.keys(newChange).length !== 1 || Object.keys(oldChange).length !== 1) return false;

    return newChange.transform === null && oldChange.transform === '';
  }

  checkMobileDragOrTap (mouseX, mouseY, isMultitouch) {
    const that = this;
    const canvas = that.canvas;
    const selectedElements = canvas.getSelectedElems();
    let continueExecution = true;

    if (that.previouslySelected) {
      const isRoot = !that.previouslySelected.length || $(that.previouslySelected).parent().is('.background');
      const isDiffSelection = !_.isEqual(_.map(selectedElements, 'id'), _.map(that.previouslySelected, 'id'));
      const overThreshold = Math.abs(that.r_start_y - mouseY) > 20 || Math.abs(that.r_start_x - mouseX) > 20;

      overThreshold && $('#selectorParentGroup, .borderLineGroup').removeClass('hidden');
      if ((isDiffSelection && !isRoot) || isMultitouch) {
        if (that.previouslySelected && overThreshold) {
          canvas.clearSelection();
          canvas.addToSelection(that.previouslySelected);
          that.previouslySelected = null;
          getClickinsideElement();
        }

        continueExecution = !that.previouslySelected && that.continueExecution;
      }
    } else {
      continueExecution = that.continueExecution;
    }

    return continueExecution;

    function getClickinsideElement () {
      const selectedElements = canvas.getSelectedElems();
      const bbox = svgedit.utilities.getStrokedBBox(selectedElements);

      that.continueExecution = bbox
        ? mouseX > bbox.x && mouseX < bbox.x + bbox.width && mouseY > bbox.y && mouseY < bbox.y + bbox.height
        : false;
    }
  }
}
